import { Check } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSiteContent } from "@/lib/firestoreService";

export const About = () => {
  const { t, lang } = useLang();
  const site = useSiteContent();
  const h1   = lang === "en" ? site.about_h1   : t.about.h1;
  const h2   = lang === "en" ? site.about_h2   : t.about.h2;
  const body = lang === "en" ? site.about_body : t.about.body;
  // Admin-edited body is a free-form multi-paragraph letter — blank lines
  // mark paragraph breaks, same convention as the textarea it's edited in.
  const bodyParagraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <section id="about" data-testid="about-section" className="relative py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/*
          Three blocks in DOM order (image, story text, highlights) so mobile
          stacks them in a sensible reading order. lg:col-start/row-start
          rearranges them into two columns only at the lg breakpoint: image
          top-left, highlights below it filling the space the taller story
          text on the right leaves behind, story text spanning both rows on
          the right so it stays top-aligned instead of vertically centered.
        */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-y-10 lg:gap-x-20">
          <div className="relative lg:col-start-1 lg:row-start-1">
            <div className="relative aspect-square max-w-md">
              <div className="absolute inset-0 -rotate-3 bg-[#556B2F]/15 rounded-sm" />
              <img
                src="https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Traditional Andhra spices"
                className="relative w-full h-full object-cover rounded-sm shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#F5E6CC] border-2 border-[#5A2D1D] px-6 py-4 rounded-sm shadow-lg">
                <div className="font-script text-2xl text-[#7A3B25] leading-none">Since</div>
                <div className="font-display text-3xl font-black text-[#5A2D1D]">Generations</div>
              </div>
            </div>
          </div>

          <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-start">
            <div className="brand-ribbon mb-5">{t.about.ribbon}</div>
            <h2
              data-testid="about-heading"
              className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#5A2D1D] leading-tight"
            >
              {h1}
              <br />
              <span className="italic text-[#7A3B25]">{h2}</span>
            </h2>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-[#2E1A12]/85 leading-relaxed" data-testid="about-body">
              {bodyParagraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {lang === "en" && (
              <div className="mt-6" data-testid="about-signature">
                <p className="font-script text-2xl text-[#7A3B25] leading-tight">With gratitude,</p>
                <p className="mt-2 font-display font-bold text-[#5A2D1D]">Chirumani Sai Raja Gubbala</p>
                <p className="text-sm text-[#2E1A12]/70">Founder &amp; CEO</p>
              </div>
            )}
          </div>

          <div className="lg:col-start-1 lg:row-start-2 lg:self-start">
            <ul className="space-y-3 max-w-md" data-testid="about-highlights">
              {t.about.highlights.map((h) => (
                <li key={h} className="flex items-center gap-3 text-[#2E1A12] font-medium">
                  <span className="w-6 h-6 rounded-full bg-[#556B2F] text-[#F5E6CC] grid place-items-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-8 max-w-md border-l-4 border-[#556B2F] pl-5 italic text-[#2E1A12]/80">
              <span className="font-script text-2xl text-[#7A3B25] block leading-none">{t.about.tagline_1}</span>
              <span className="font-script text-2xl text-[#7A3B25] block leading-none">{t.about.tagline_2}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
